import React, { useState, useRef, useEffect } from "react";
import { Chip, Box, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import styles from "./CategoryFilters.module.scss";
import { PostCategory } from "../../../constants/categories";

interface CategoryFiltersProps {
    categories: PostCategory[];
    activeCategories: PostCategory[];
    onCategoryToggle: (category: PostCategory | "All") => void;
}

const CategoryFilters: React.FC<CategoryFiltersProps> = ({
    categories,
    activeCategories,
    onCategoryToggle,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(false);

    const checkArrows = () => {
        const container = scrollContainerRef.current;
        if (container) {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            const isOverflowing = scrollWidth > clientWidth;
            setShowLeftArrow(isOverflowing && scrollLeft > 5); // Show arrow only after some scrolling
            setShowRightArrow(isOverflowing && scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            checkArrows();
        }, 150);

        window.addEventListener("resize", checkArrows);
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener("scroll", checkArrows);
        }

        return () => {
            clearTimeout(timer);
            window.removeEventListener("resize", checkArrows);
            if (container) {
                container.removeEventListener("scroll", checkArrows);
            }
        };
    }, [categories]);

    const handleCategoryClick = (category: PostCategory | "All") => {
        onCategoryToggle(category);
    };

    const scroll = (direction: "left" | "right") => {
        if (scrollContainerRef.current) {
            const scrollAmount = scrollContainerRef.current.clientWidth * 0.7;
            scrollContainerRef.current.scrollBy({
                left: direction === "left" ? -scrollAmount : scrollAmount,
                behavior: "smooth",
            });
        }
    };

    const isAllSelected = activeCategories.length === 0 || activeCategories.includes("All");

    return (
        <div className={styles.scrollWrapper}>
            <IconButton
                onClick={() => scroll("left")}
                className={styles.scrollButton}
                style={{ visibility: showLeftArrow ? "visible" : "hidden" }}
                aria-label="Scroll left"
            >
                <ChevronLeft />
            </IconButton>

            <Box className={styles.filtersContainer} ref={scrollContainerRef}>
                <Chip
                    label="All"
                    clickable
                    color={isAllSelected ? "primary" : "default"}
                    onClick={() => handleCategoryClick("All")}
                    className={`${styles.filterChip} ${isAllSelected ? styles.active : ""}`}
                />
                {categories.map((category) => (
                    <Chip
                        key={category}
                        label={category}
                        clickable
                        color={activeCategories.includes(category) ? "primary" : "default"}
                        onClick={() => handleCategoryClick(category)}
                        className={`${styles.filterChip} ${
                            activeCategories.includes(category) ? styles.active : ""
                        }`}
                    />
                ))}
            </Box>

            <IconButton
                onClick={() => scroll("right")}
                className={styles.scrollButton}
                style={{ visibility: showRightArrow ? "visible" : "hidden" }}
                aria-label="Scroll right"
            >
                <ChevronRight />
            </IconButton>
        </div>
    );
};

export default CategoryFilters;
